import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Карточка «Структура» запуска с группами шаблона (спека v4 §11.5, UA Б3) —
 * ЕДИНСТВЕННАЯ «Структура» такого запуска (E-2 P2-3): «1 кампания · 2 группы
 * шаблона · всего 6 групп объявлений · 6 объявлений (1-6-1) · бюджет на группу
 * объявлений (ABO)» (число групп объявлений — сервера, E-2 P2-2), деньги в день
 * — `launch.spend` сервера, и таблица «строка = группа объявлений»: Группа ·
 * Копия · Отличия · Advantage+ · Ставка · В день (ABO) · Создана в Meta ·
 * Сверка. Ставка, Advantage+ и бюджет — из отправленной в Meta спеки запуска
 * (`compiled`); отличия — из снимка запуска (`adSetGroups[].differences`, E-2
 * P2-4), без снимка (старый auto-ads) — из шаблона той же версии, иначе
 * «шаблон изменён после запуска».
 */
import { useQuery } from '@tanstack/react-query';
import { SectionCard, SortSelect, Table, formatCount, useTableColumns, } from '@wit-support/ui';
import { EntityLabel, ListCard, ResponsiveTableList } from '@wit-support/ui-domain';
import { getTemplate } from '../api.js';
import { formatMinor } from '../format.js';
import { ADS_QUERY_DEFAULTS } from '../query-policy.js';
import { templateKey } from '../shared.js';
import { useDisplayContext } from '../registry/registry-form.js';
import { displayValue, humanizeDisplay } from '../registry/value-display.js';
import { useRegistry } from '../shared.js';
import { ADVANTAGE_AUDIENCE_KEY, GROUP_BID_KEY, frozenGroupOrder, groupOrder, groupTitle, groupsCountText, } from '../groups.js';
import { SpendSummary } from './spend-summary.js';
import { STEP_AMBIGUOUS_CREATE_TEXT, STEP_CREATING_TEXT } from '../step-status.js';
import { ColumnHeader, TableColumnsBar, adsTableColumnsKey, groupedSortValue, sortRows, useColumnSort, } from './table-kit.js';
const num = (v) => {
    const n = typeof v === 'string' ? Number(v) : v;
    return typeof n === 'number' && Number.isFinite(n) ? n : null;
};
export function compiledAdSetsOf(compiled) {
    const out = new Map();
    const sets = compiled?.adSets;
    if (!Array.isArray(sets))
        return out;
    for (const s of sets) {
        const set = (s ?? null);
        if (typeof set?.localRef !== 'string')
            continue;
        const a = set.params?.targeting?.targeting_automation?.advantage_audience;
        out.set(set.localRef, {
            bid: num(set.params?.bid_amount),
            advantage: a === 1 ? true : a === 0 ? false : null,
            budget: num(set.params?.daily_budget),
            ads: Array.isArray(set.ads) ? set.ads.length : 0,
        });
    }
    return out;
}
const COLUMNS = [
    { id: 'group', label: 'Группа', hideable: false, defaultPin: 'left' },
    { id: 'copy', label: 'Копия' },
    { id: 'differences', label: 'Отличия' },
    { id: 'advantage', label: 'Advantage+' },
    { id: 'bid', label: 'Ставка' },
    { id: 'budget', label: 'В день (ABO)' },
    { id: 'meta', label: 'Создана в Meta' },
    { id: 'verify', label: 'Сверка' },
];
export function adSetCreateState(l, ref) {
    const step = l.steps.find((s) => s.localRef === ref && (s.kind === 'adset' || s.kind === 'create.adset'));
    const status = step?.status;
    if (status === 'rolled_back')
        return 'rolled-back';
    if (status === 'ambiguous')
        return 'ambiguous';
    if (l.created.adSetIds[ref] != null)
        return 'created';
    if (status === 'in_progress')
        return 'creating';
    if (status === 'failed') {
        const e = step?.error;
        const meta = e != null && (e.fbtraceId != null || /^\d+$/.test(e.code));
        return meta ? 'refused' : 'failed';
    }
    // запуск откатан — «ещё не создана» вводит в заблуждение: создаваться она уже не будет (E-2 P3)
    if (l.displayStatus === 'rolled_back')
        return 'not-created-rolled-back';
    // откат ещё идёт — «откатан» было бы рано (ревью E-2 P3)
    if (l.displayStatus === 'rolling_back')
        return 'not-created-rolling-back';
    return 'not-yet';
}
const STATE_TEXT = {
    'rolled-back': 'удалена откатом',
    'not-created-rolled-back': 'не создавалась — запуск откатан',
    'not-created-rolling-back': 'откатывается…',
    ambiguous: STEP_AMBIGUOUS_CREATE_TEXT,
    creating: STEP_CREATING_TEXT,
    refused: 'не создана — Meta отказала',
    failed: 'не создана — ошибка',
    'not-yet': 'ещё не создана',
};
/** Тон строки: красный — отказ/ошибка, жёлтый — неизвестно. */
const STATE_DANGER = new Set(['refused', 'failed']);
export function launchGroupRows(l, order = frozenGroupOrder(l.adSetGroups ?? []) ??
    groupOrder(l.adSetGroups ?? [])) {
    const refs = l.adSetGroups ?? [];
    const compiled = compiledAdSetsOf(l.compiled);
    const discrepancies = l.verify?.discrepancies ?? null;
    return refs.map((r) => ({
        ref: r.localRef,
        index: order.get(r.groupKey) ?? 0,
        groupKey: r.groupKey,
        label: r.label,
        copyIndex: r.copyIndex,
        compiled: compiled.get(r.localRef) ?? null,
        metaId: l.created.adSetIds[r.localRef] ?? null,
        state: adSetCreateState(l, r.localRef),
        issues: discrepancies == null
            ? null
            : discrepancies.filter((d) => d.localRef === r.localRef || d.localRef.startsWith(`${r.localRef}/`)).length,
    }));
}
const ADSET_FORMS = ['группа объявлений', 'группы объявлений', 'групп объявлений'];
/**
 * «1 кампания · 2 группы шаблона × 3 копии = 6 групп объявлений · 6 объявлений
 * (1-6-1)». Число групп объявлений — сервера (`spend.adSets`; нет — строки
 * `adSetGroups`, по одной на группу объявлений запуска), а не «группы × копии»:
 * в ABO 1-6-1 группы объявлений умножаются и на креативы (E-2 P2-2) — тогда
 * «2 группы шаблона · всего 6 групп объявлений».
 */
export function launchGroupsSummary(rows, adSets = rows.length) {
    const groups = new Set(rows.map((r) => r.groupKey)).size;
    const copies = new Set(rows.map((r) => r.copyIndex)).size;
    const ads = rows.reduce((n, r) => n + (r.compiled?.ads ?? 0), 0);
    const perAdSet = rows[0]?.compiled?.ads ?? 0;
    const shape = `1-${adSets}-${perAdSet}`;
    const head = groups * copies === adSets
        ? groupsCountText(groups, copies)
        : `${formatCount(groups, ['группа', 'группы', 'групп'])} шаблона · всего ${formatCount(adSets, ADSET_FORMS)}`;
    return `1 кампания · ${head} · ${formatCount(ads, ['объявление', 'объявления', 'объявлений'])} (${shape})`;
}
/**
 * Подпись строки группы объявлений: «① LAL 1 %»; несколько групп объявлений
 * одной группы шаблона и копии (по креативу на каждую, ABO 1-6-1) — «① LAL 1 % ·
 * креатив 2» по порядку в запуске (E-2 P3-5).
 */
export function groupRowTitle(r, rows) {
    const head = groupTitle(r.index, r.label);
    const twins = rows.filter((x) => x.groupKey === r.groupKey && x.copyIndex === r.copyIndex);
    return twins.length < 2
        ? head
        : `${head} · креатив ${twins.findIndex((x) => x.ref === r.ref) + 1}`;
}
/** «бюджет на кампанию (CBO)» / «бюджет на группу объявлений (ABO)» — по деньгам сервера. */
export function budgetLevelText(level) {
    return level === 'CBO'
        ? 'бюджет на кампанию (CBO)'
        : level === 'ABO'
            ? 'бюджет на группу объявлений (ABO)'
            : null;
}
/** Поля отличий, показанные своими колонками (Advantage+ и ставка) — в «Отличиях» не повторяются. */
const OWN_COLUMN_KEYS = new Set([ADVANTAGE_AUDIENCE_KEY, GROUP_BID_KEY]);
/**
 * Отличия группы словами из снимка запуска (`differences` группы, E-2 P2-4):
 * «Lookalike: LAL 1 %»; пусто — «как общее»; снимка нет — null.
 */
export function snapshotDifferencesText(refs, groupKey, displayOf = (d) => d.display) {
    const ref = refs.find((r) => r.groupKey === groupKey && r.differences != null);
    if (ref?.differences == null)
        return null;
    const parts = ref.differences
        .filter((d) => !OWN_COLUMN_KEYS.has(d.fieldKey))
        .map((d) => `${d.label}: ${displayOf(d)}`);
    return parts.length > 0 ? parts.join(' · ') : 'как общее';
}
/**
 * Группы запуска с номерами шаблона (`index`) — для «Созданных объектов» и
 * прочих подписей «③ Yoga» (ревью SB-7 P2-1): шаблон той же версии, иначе —
 * по первому появлению.
 */
export function useLaunchGroupRefs(l) {
    const ref = l.templateRef ?? null;
    const refs = l.adSetGroups ?? [];
    const tpl = useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: templateKey(ref?.id ?? 'none'),
        queryFn: () => getTemplate(ref.id),
        enabled: ref != null && refs.length > 0,
    });
    if (refs.length === 0)
        return null;
    // номер, замороженный пачкой (ревью E-2 P2), — тот же, что на странице пачки
    const order = frozenGroupOrder(refs) ?? groupOrder(refs, tpl.data, ref?.version);
    return refs.map((r) => ({ ...r, index: order.get(r.groupKey) ?? 0 }));
}
export function LaunchGroupsCard({ launch: l }) {
    const ref = l.templateRef ?? null;
    const tpl = useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: templateKey(ref?.id ?? 'none'),
        queryFn: () => getTemplate(ref.id),
        enabled: ref != null && (l.adSetGroups?.length ?? 0) > 0,
    });
    const registry = useRegistry();
    const ctx = useDisplayContext(l.currency ?? null);
    const rows = launchGroupRows(l, frozenGroupOrder(l.adSetGroups ?? []) ??
        groupOrder(l.adSetGroups ?? [], tpl.data, ref?.version));
    const oneCopy = new Set(rows.map((r) => r.copyIndex)).size <= 1;
    const abo = rows.some((r) => r.compiled?.budget != null);
    const defs = COLUMNS.filter((c) => !(c.id === 'copy' && oneCopy) && !(c.id === 'budget' && !abo));
    const cols = useTableColumns(adsTableColumnsKey('launch-groups'), defs);
    const [sort, setSort] = useColumnSort(cols);
    if (rows.length === 0)
        return null;
    // отличия — из снимка запуска (E-2 P2-4); старый auto-ads без снимка — из шаблона ТОЙ ЖЕ
    // версии; иначе честно: «шаблон изменён после запуска»
    const sameTemplate = tpl.data && ref && tpl.data.version === ref.version ? tpl.data : null;
    const diffOf = (key) => {
        // словами, как путь через шаблон: подпись сервера, иначе код → подпись реестра (ревью E-2 P3)
        const snap = snapshotDifferencesText(l.adSetGroups ?? [], key, (d) => d.displayLabel != null && d.displayLabel !== ''
            ? d.displayLabel
            : humanizeDisplay(d.display, (registry.data?.fields ?? []).find((x) => x.key === d.fieldKey), ctx));
        if (snap != null)
            return snap;
        if (!sameTemplate)
            return tpl.data ? 'шаблон изменён после запуска' : '—';
        const g = sameTemplate.adSetGroups?.groups.find((x) => x.key === key);
        if (!g)
            return '—';
        const descs = registry.data?.fields ?? [];
        const parts = Object.entries(g.values)
            .filter(([k]) => k !== ADVANTAGE_AUDIENCE_KEY && k !== GROUP_BID_KEY)
            .map(([k, v]) => {
            const d = descs.find((x) => x.key === k);
            return d ? `${d.label}: ${displayValue(d, v, ctx)}` : null;
        })
            .filter((x) => x != null);
        return parts.length > 0 ? parts.join(' · ') : 'как общее';
    };
    const cur = l.currency ?? null;
    const valueOf = (r, key) => {
        switch (key) {
            case 'group':
                return r.index;
            case 'copy':
                return r.copyIndex;
            case 'differences':
                return diffOf(r.groupKey);
            case 'advantage':
                return r.compiled?.advantage == null ? null : r.compiled.advantage ? 1 : 0;
            case 'bid':
                return r.compiled?.bid == null ? null : groupedSortValue(cur ?? '', r.compiled.bid);
            case 'budget':
                return r.compiled?.budget == null ? null : groupedSortValue(cur ?? '', r.compiled.budget);
            case 'meta':
                return r.metaId != null ? 1 : 0;
            case 'verify':
                return r.issues;
        }
    };
    const items = sortRows(rows, sort, valueOf);
    const title = (r) => `${groupRowTitle(r, rows)}${oneCopy ? '' : ` · копия ${r.copyIndex + 1}`}`;
    // сверка — только о созданной группе объявлений (после отката / без объекта «✓» неверно)
    const verifyText = (r) => r.issues == null || r.state !== 'created'
        ? '—'
        : r.issues === 0
            ? '✓'
            : `⛔ ${formatCount(r.issues, ['расхождение', 'расхождения', 'расхождений'])}`;
    const cell = (c, r) => {
        switch (c.id) {
            case 'group':
                return (_jsx(Table.Cell, { column: c, className: "font-medium", children: groupRowTitle(r, rows) }, c.id));
            case 'copy':
                return (_jsx(Table.Cell, { column: c, children: r.copyIndex + 1 }, c.id));
            case 'differences':
                return (_jsx(Table.Cell, { column: c, children: diffOf(r.groupKey) }, c.id));
            case 'advantage':
                return (_jsx(Table.Cell, { column: c, children: r.compiled?.advantage == null ? '—' : r.compiled.advantage ? 'вкл' : 'выкл' }, c.id));
            case 'bid':
                return (_jsx(Table.Cell, { column: c, className: "text-right tabular-nums", children: r.compiled?.bid != null ? formatMinor(r.compiled.bid, cur) : '—' }, c.id));
            case 'budget':
                return (_jsx(Table.Cell, { column: c, className: "text-right tabular-nums", children: r.compiled?.budget != null ? formatMinor(r.compiled.budget, cur) : '—' }, c.id));
            case 'meta':
                return (_jsx(Table.Cell, { column: c, children: r.state === 'created' && r.metaId != null ? (_jsx(EntityLabel, { kind: "adset", id: r.metaId, name: "\u2713", variant: "inline", avatar: false })) : (STATE_TEXT[r.state]) }, c.id));
            case 'verify':
                return (_jsx(Table.Cell, { column: c, children: verifyText(r) }, c.id));
        }
    };
    return (_jsx(SectionCard, { title: "\u0421\u0442\u0440\u0443\u043A\u0442\u0443\u0440\u0430", children: _jsxs("div", { className: "flex flex-col gap-3", "data-testid": "launch-groups-card", children: [_jsx("span", { className: "text-sm", "data-testid": "launch-structure", children: [
                        launchGroupsSummary(rows, l.spend?.adSets ?? rows.length),
                        budgetLevelText(l.spend?.level),
                    ]
                        .filter((x) => x != null)
                        .join(' · ') }), l.spend ? _jsx(SpendSummary, { spend: { lines: [l.spend] } }) : null, _jsx(TableColumnsBar, { columns: cols, sort: sort, className: "hidden md:flex" }), _jsx(SortSelect, { id: "launch-groups-card-sort", label: "\u0421\u043E\u0440\u0442\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u043F\u043E", sort: sort, onSortChange: setSort, options: cols.visible.map((c) => ({
                        key: c.id,
                        label: c.def.label,
                        defaultDirection: c.id === 'group' || c.id === 'copy' ? 'asc' : 'desc',
                    })) }), _jsx(ResponsiveTableList, { items: items, getKey: (r) => r.ref, headerCells: cols.visible.map((c) => (_jsx(ColumnHeader, { column: c, sort: sort, onSortChange: setSort, align: c.id === 'bid' || c.id === 'budget' ? 'right' : 'left' }, c.id))), renderRow: (r) => (_jsx(Table.Row, { "data-testid": `launch-group-row-${r.ref}`, children: cols.visible.map((c) => cell(c, r)) }, r.ref)), renderMobileCard: (r) => (_jsxs(ListCard, { className: `text-sm ${STATE_DANGER.has(r.state) ? 'border-[var(--color-state-danger-fg)]' : r.state === 'ambiguous' ? 'border-[var(--color-state-warning-fg)]' : ''}`, children: [_jsxs("span", { className: "font-medium [overflow-wrap:anywhere]", children: [title(r), " \u2014 ", diffOf(r.groupKey)] }), _jsx("span", { className: "text-xs", children: [
                                    r.compiled?.advantage == null
                                        ? null
                                        : r.compiled.advantage
                                            ? 'A+ вкл'
                                            : 'A+ выкл',
                                    r.compiled?.bid != null ? formatMinor(r.compiled.bid, cur) : null,
                                    r.compiled?.budget != null ? `${formatMinor(r.compiled.budget, cur)}/день` : null,
                                ]
                                    .filter(Boolean)
                                    .join(' · ') }), _jsxs("span", { className: "flex flex-wrap items-center gap-1 text-xs", children: ["Meta:", ' ', r.state === 'created' && r.metaId != null ? (_jsx(EntityLabel, { kind: "adset", id: r.metaId, name: "\u2713", variant: "inline", avatar: false })) : (STATE_TEXT[r.state]), ' ', "\u00B7 \u0421\u0432\u0435\u0440\u043A\u0430: ", verifyText(r)] })] })) })] }) }));
}
//# sourceMappingURL=launch-groups-card.js.map